/**
 * useWalletBalance Hook
 *
 * Lazy polling strategy for wallet balance updates:
 * - Fetches balance on mount (when user visits a page that shows balance)
 * - Optional polling every X seconds (only when component is mounted)
 * - Provides manual refresh function for event-driven updates
 */

import { useEffect, useCallback, useRef } from 'react';
import { useAuthStore } from './auth';
import { apiClient } from './api';

interface UseWalletBalanceOptions {
  /** Enable automatic polling (default: false) */
  enablePolling?: boolean;
  /** Polling interval in milliseconds (default: 30000 = 30 seconds) */
  pollingInterval?: number;
  /** Fetch balance on mount (default: true) */
  fetchOnMount?: boolean;
}

/**
 * Hook to manage wallet balance updates
 *
 * @example
 * // Simple usage - fetch on mount only
 * const { balance, refresh, isRefreshing } = useWalletBalance();
 *
 * @example
 * // With polling (for critical pages like billing)
 * const { balance, refresh } = useWalletBalance({
 *   enablePolling: true,
 *   pollingInterval: 60000 // 1 minute
 * });
 *
 * @example
 * // Event-driven (refresh after AI operation)
 * const { refresh } = useWalletBalance();
 * const handleExtraction = async () => {
 *   await extractBrand();
 *   await refresh(); // Update balance after operation
 * };
 */
export function useWalletBalance(options: UseWalletBalanceOptions = {}) {
  const {
    enablePolling = false,
    pollingInterval = 30000, // 30 seconds default
    fetchOnMount = true,
  } = options;

  const { workspace, updateWalletBalance } = useAuthStore();
  const pollingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isRefreshingRef = useRef(false);

  /**
   * Fetch wallet balance from API and update store
   */
  const refresh = useCallback(async () => {
    // Prevent concurrent refreshes
    if (isRefreshingRef.current) {
      console.log('[WALLET] Refresh already in progress, skipping');
      return;
    }

    try {
      isRefreshingRef.current = true;
      console.log('[WALLET] Fetching wallet balance...');

      const response = await apiClient.get('/billing/wallet');
      const walletData = response.data?.data;

      if (walletData?.balanceTokens !== undefined) {
        console.log('[WALLET] Balance updated:', walletData.balanceTokens);
        updateWalletBalance(walletData.balanceTokens);
      } else {
        console.warn('[WALLET] No balance data in response:', response.data);
      }
    } catch (error) {
      console.error('[WALLET] Failed to fetch wallet balance:', error);
      // Don't throw - we want this to be silent for UX
    } finally {
      isRefreshingRef.current = false;
    }
  }, [updateWalletBalance]);

  /**
   * Start polling interval
   */
  const startPolling = useCallback(() => {
    if (!enablePolling || pollingTimerRef.current) {
      return;
    }

    console.log(`[WALLET] Starting polling (interval: ${pollingInterval}ms)`);
    pollingTimerRef.current = setInterval(() => {
      refresh();
    }, pollingInterval);
  }, [enablePolling, pollingInterval, refresh]);

  /**
   * Stop polling interval
   */
  const stopPolling = useCallback(() => {
    if (pollingTimerRef.current) {
      console.log('[WALLET] Stopping polling');
      clearInterval(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }
  }, []);

  // Fetch on mount
  useEffect(() => {
    if (fetchOnMount) {
      refresh();
    }
  }, [fetchOnMount, refresh]);

  // Setup polling
  useEffect(() => {
    if (enablePolling) {
      startPolling();
    }

    // Cleanup on unmount
    return () => {
      stopPolling();
    };
  }, [enablePolling, startPolling, stopPolling]);

  return {
    /** Current balance from store (may be stale until first fetch completes) */
    balance: workspace?.walletBalance ?? 0,
    /** Manually refresh balance (use after AI operations) */
    refresh,
    /** Whether a refresh is currently in progress */
    isRefreshing: isRefreshingRef.current,
  };
}
